import * as THREE from 'three/webgpu';

/**
 * Photo mode helper: render one frame into an offscreen sRGB target, read the
 * pixels back and save them as a JPEG. In dev the Vite server stores it in
 * docs/screenshots/<name>.jpg (see vite.config.ts); elsewhere it downloads.
 *
 * Reading the WebGPU canvas itself (toBlob/drawImage) returns the last frame the
 * browser composited, which is stale in background tabs, so we don't.
 */
export async function saveScreenshot(
  renderer: THREE.WebGPURenderer,
  canvas: HTMLCanvasElement,
  name: string,
  renderFrame: () => void,
): Promise<string> {
  const w = canvas.width;
  const h = canvas.height;
  const rt = new THREE.RenderTarget(w, h, { type: THREE.UnsignedByteType, depthBuffer: false });
  rt.texture.colorSpace = THREE.SRGBColorSpace;
  renderer.setOutputRenderTarget(rt);
  try {
    renderFrame();
  } finally {
    renderer.setOutputRenderTarget(null);
  }
  const pixels = (await renderer.readRenderTargetPixelsAsync(rt, 0, 0, w, h)) as Uint8Array;
  rt.dispose();
  const bytesPerRow = pixels.length / h;
  const rgba = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) rgba.set(pixels.subarray(y * bytesPerRow, y * bytesPerRow + w * 4), y * w * 4);
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  cv.getContext('2d')!.putImageData(new ImageData(rgba, w, h), 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => cv.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) return 'capture failed';
  if (import.meta.env.DEV) {
    const res = await fetch(`/__screenshot?name=${encodeURIComponent(name)}`, { method: 'POST', body: blob });
    return res.ok ? `saved docs/screenshots/${name}.jpg` : `server said ${res.status}`;
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.jpg`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  return 'downloaded';
}
